import React from 'react';
import { DocPageLayout } from '../../components/DocPageLayout';
import { CodeBlock } from '../../components/CodeBlock';
import { InfoBox } from '../../components/InfoBox';
export function ApiSessionPage() {
  return <DocPageLayout title="Session API" description="Operations related to creating and managing payment sessions for secure transaction processing." breadcrumbs={[{
    label: 'API Reference',
    id: 'api-overview'
  }, {
    label: 'Session'
  }]} sections={[{
    id: 'create-session',
    title: 'Create Session'
  }, {
    id: 'update-session',
    title: 'Update Session'
  }, {
    id: 'retrieve-session',
    title: 'Retrieve Session'
  }, {
    id: 'session-fields',
    title: 'Session Fields'
  }]} data-id="element-675">
      <h2 id="create-session" className="text-2xl font-bold text-gray-900 mt-6 mb-4 border-b border-gray-200 pb-2" data-id="element-676">
        Create Session
      </h2>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 font-mono text-sm" data-id="element-677">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700 mr-2" data-id="element-678">
          POST
        </span>
        <span className="text-gray-600" data-id="element-679">/merchant/</span>
        <span className="text-blue-600" data-id="element-680">{'{merchantId}'}</span>
        <span className="text-gray-600" data-id="element-681">/session</span>
      </div>
      <CodeBlock language="JSON" title="Request" code={`{
  "order": {
    "amount": "100.00",
    "currency": "USD"
  }
}`} data-id="element-682" />
      <CodeBlock language="JSON" title="Response" code={`{
  "merchant": "TEST12345678",
  "result": "SUCCESS",
  "session": {
    "id": "SESSION0002abcdef1234567890",
    "updateStatus": "NO_UPDATE",
    "version": "v1",
    "aes256Key": "encrypted-key-value"
  }
}`} data-id="element-683" />

      <h2 id="update-session" className="text-2xl font-bold text-gray-900 mt-10 mb-4 border-b border-gray-200 pb-2" data-id="element-684">
        Update Session
      </h2>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 font-mono text-sm" data-id="element-685">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700 mr-2" data-id="element-686">
          PUT
        </span>
        <span className="text-gray-600" data-id="element-687">/merchant/</span>
        <span className="text-blue-600" data-id="element-688">{'{merchantId}'}</span>
        <span className="text-gray-600" data-id="element-689">/session/</span>
        <span className="text-blue-600" data-id="element-690">{'{sessionId}'}</span>
      </div>
      <p className="text-gray-700 mb-4" data-id="element-691">
        Update session with payment details collected from the payer. Typically
        used with Hosted Session integration.
      </p>

      <h2 id="retrieve-session" className="text-2xl font-bold text-gray-900 mt-10 mb-4 border-b border-gray-200 pb-2" data-id="element-692">
        Retrieve Session
      </h2>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 font-mono text-sm" data-id="element-693">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700 mr-2" data-id="element-694">
          GET
        </span>
        <span className="text-gray-600" data-id="element-695">/merchant/</span>
        <span className="text-blue-600" data-id="element-696">{'{merchantId}'}</span>
        <span className="text-gray-600" data-id="element-697">/session/</span>
        <span className="text-blue-600" data-id="element-698">{'{sessionId}'}</span>
      </div>

      <h2 id="session-fields" className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-699">
        Session Fields
      </h2>
      <div className="overflow-x-auto mb-8" data-id="element-700">
        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-id="element-701">
          <thead data-id="element-702">
            <tr className="bg-gray-100" data-id="element-703">
              <th className="text-left px-4 py-3 font-semibold border-b" data-id="element-704">
                Field
              </th>
              <th className="text-left px-4 py-3 font-semibold border-b" data-id="element-705">
                Type
              </th>
              <th className="text-left px-4 py-3 font-semibold border-b" data-id="element-706">
                Description
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200" data-id="element-707">
            <tr data-id="element-708">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-709">session.id</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-710">String</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-711">
                Unique session identifier
              </td>
            </tr>
            <tr className="bg-gray-50" data-id="element-712">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-713">
                session.updateStatus
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-714">Enum</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-715">
                NO_UPDATE, SUCCESS, FAILURE
              </td>
            </tr>
            <tr data-id="element-716">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-717">session.version</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-718">String</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-719">
                Session version for optimistic locking
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <InfoBox type="warning" title="Session Expiry" data-id="element-720">
        Sessions expire after 30 minutes of inactivity. Always create a fresh
        session if the previous one has expired.
      </InfoBox>
    </DocPageLayout>;
}